import React from "react";
import { Link } from "react-router-dom";
import {
  Heart, ArrowRight, Phone, MapPin, X, Sparkles, ShieldCheck, Trash2,
  Bed, Bath, Layers, Users, Clock, Calendar, Car, Home as HomeIcon,
  Accessibility, ArrowDownToLine, DoorOpen, ShowerHead, Lightbulb,
  Volume2, Lock, Trees, CheckCircle2,
} from "lucide-react";
import { useSavedHomes } from "../lib/savedHomes";
import { statusBadge } from "../data/properties";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { trackPhoneClick } from "../lib/tracking";

const QF_ICON_MAP = {
  Bed,
  Bath,
  Layers,
  Users,
  Clock,
  Calendar,
  Car,
  HomeIcon,
};

const ACCESSIBILITY_ICON_MAP = [
  { match: /step-?free|level entry|threshold/i, Icon: ArrowDownToLine },
  { match: /doorway|door width|wide internal/i, Icon: DoorOpen },
  { match: /shower|walk-?in/i, Icon: ShowerHead },
  { match: /vanity|bathroom|amenities|bathing/i, Icon: Bath },
  { match: /lighting|led/i, Icon: Lightbulb },
  { match: /acoustic|sound|quiet|low-?stim|sensory/i, Icon: Volume2 },
  { match: /privacy|secure|private/i, Icon: Lock },
  { match: /single-?level|outdoor|lawn|yard|garden/i, Icon: Trees },
];
function pickAccessibilityIcon(title) {
  for (const m of ACCESSIBILITY_ICON_MAP) if (m.match.test(title)) return m.Icon;
  return Accessibility;
}

// Helpers to extract specific facts from a property
function specValue(p, regex) {
  const hit = (p.specs || []).find((s) => regex.test(s.k));
  return hit ? hit.v : null;
}

function quickFactValue(p, key) {
  const hit = (p.quickFacts || []).find(
    (qf) => qf.k.toLowerCase() === key.toLowerCase()
  );
  return hit ? hit.v : null;
}

function inferType(p) {
  const v = specValue(p, /^service$/i);
  if (v) return v;
  if (/\bsda\b/i.test(p.short || "")) return "Specialist Disability Accommodation";
  return "Supported Independent Living";
}

function PropertyColumn({ p, onRemove }) {
  const sb = statusBadge(p);
  const status = sb.label;
  const support = quickFactValue(p, "Support ratio") || specValue(p, /support ratio/i) || "1:1 & 1:2 only";
  const layout = quickFactValue(p, "Layout") || specValue(p, /layout|location/i) || "—";
  const bathrooms = quickFactValue(p, "Bathrooms") || "—";
  const parking = quickFactValue(p, "Parking") || null;
  const onCall = quickFactValue(p, "On-call") || quickFactValue(p, "On call") || "24 / 7";
  const availability = quickFactValue(p, "Available") || quickFactValue(p, "Status") || (p.status === "available" ? "Now" : "Coming soon");
  const type = inferType(p);

  return (
    <article
      className="snap-start shrink-0 w-[88vw] sm:w-[420px] alpha-card !p-0 overflow-hidden flex flex-col bg-white"
      data-testid={`saved-col-${p.slug}`}
    >
      {/* Hero photo */}
      <div className="relative aspect-[5/3] overflow-hidden bg-[color:var(--alpha-bg-soft)]">
        <Link to={p.href} aria-label={`Open ${p.name}`}>
          <img
            src={p.images[0].src}
            alt={p.images[0].alt}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0625]/80 via-[#0F0625]/15 to-transparent" />
        </Link>

        {/* Status pill */}
        <span
          className={`absolute top-3 left-3 alpha-chip backdrop-blur !py-1 !px-2.5 !text-[10.5px] font-bold uppercase tracking-[0.2em] shadow ${
            sb.tone === "green"
              ? "!bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)]"
              : sb.tone === "purple"
              ? "!bg-[color:var(--alpha-purple-700)] !text-white !border-[color:var(--alpha-purple-800)]"
              : "!bg-white !text-[color:var(--alpha-ink)] !border-white"
          }`}
        >
          {status}
        </span>

        {/* Remove */}
        <button
          type="button"
          onClick={() => onRemove(p.slug)}
          aria-label={`Remove ${p.name} from saved homes`}
          data-testid={`saved-remove-${p.slug}`}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/95 backdrop-blur text-[color:var(--alpha-ink)] hover:bg-white border border-white/70 flex items-center justify-center shadow-md focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40"
        >
          <X className="w-4 h-4" aria-hidden />
        </button>

        {/* Bottom title */}
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-white/85 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {p.suburb}, {p.state}
          </div>
          <Link
            to={p.href}
            className="block font-display font-semibold text-[20px] !text-white leading-tight mt-1 hover:underline"
          >
            {p.name}
          </Link>
        </div>
      </div>

      {/* Spec rows — keys aligned across columns via CSS grid */}
      <div className="px-4 md:px-5 py-4 space-y-2.5 text-[13.5px] leading-tight">
        <SpecRow label="Type" value={type} />
        <SpecRow label="Layout" value={layout} />
        <SpecRow label="Bathrooms" value={bathrooms} />
        {parking && <SpecRow label="Parking" value={parking} />}
        <SpecRow label="Support ratio" value={support} />
        <SpecRow label="On-call" value={onCall} />
        <SpecRow label="Available" value={availability} />
      </div>

      {/* Accessibility chips */}
      {p.accessibility?.length > 0 && (
        <div className="px-4 md:px-5 pb-4 border-t border-[color:var(--alpha-line)]/70 pt-3">
          <div className="text-[10.5px] tracking-[0.2em] uppercase font-bold text-[color:var(--alpha-mute)] mb-2">
            Accessibility
          </div>
          <ul className="grid grid-cols-1 gap-1.5">
            {p.accessibility.slice(0, 6).map((a) => {
              const Icon = pickAccessibilityIcon(a.title);
              return (
                <li
                  key={a.title}
                  className="flex items-start gap-2 text-[12.5px] text-[color:var(--alpha-ink)]"
                >
                  <span className="w-6 h-6 rounded-md bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-3 h-3" aria-hidden />
                  </span>
                  <span className="leading-snug">{a.title}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Highlights */}
      {p.features?.length > 0 && (
        <div className="px-4 md:px-5 pb-4">
          <div className="text-[10.5px] tracking-[0.2em] uppercase font-bold text-[color:var(--alpha-mute)] mb-2">
            Highlights
          </div>
          <div className="flex flex-wrap gap-1.5">
            {p.features.slice(0, 6).map((f) => (
              <span
                key={f}
                className="alpha-chip alpha-chip-neutral !text-[11px] !py-1 !px-2.5"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="mt-auto p-4 md:p-5 border-t border-[color:var(--alpha-line)] flex flex-col gap-2 bg-[color:var(--alpha-bg-soft)]">
        <Link
          to={`/homes/${p.slug}/enquire`}
          className="alpha-btn-primary w-full justify-center !py-3"
          data-testid={`saved-enquire-${p.slug}`}
        >
          {p.status === "upcoming" ? "Register interest" : "Enquire now"}
          <ArrowRight className="w-4 h-4" />
        </Link>
        <a
          href="tel:1300380692"
          onClick={() => trackPhoneClick("1300 380 692", `saved-homes-${p.slug}`)}
          className="alpha-btn-ghost w-full justify-center !py-3"
        >
          <Phone className="w-4 h-4" /> 1300 380 692
        </a>
      </div>
    </article>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 items-baseline">
      <dt className="text-[10.5px] tracking-[0.2em] uppercase font-bold text-[color:var(--alpha-mute)]">
        {label}
      </dt>
      <dd className="font-semibold text-[color:var(--alpha-ink)]">{value}</dd>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="alpha-card p-8 md:p-12 text-center max-w-2xl mx-auto" data-testid="saved-empty">
      <div className="w-14 h-14 rounded-full alpha-gradient text-white flex items-center justify-center mx-auto shadow-md">
        <Heart className="w-6 h-6" aria-hidden />
      </div>
      <h2 className="mt-5 font-display text-2xl md:text-[32px] font-medium leading-tight">
        Your shortlist starts here.
      </h2>
      <p className="mt-3 text-[color:var(--alpha-mute)] text-[15px] leading-relaxed max-w-md mx-auto">
        Browse our homes and tap the heart on any you'd like to compare. Your shortlist is saved on this device — no account needed.
      </p>
      <div className="mt-6 flex flex-wrap gap-2.5 justify-center">
        <Link to="/vacancies" className="alpha-btn-primary" data-testid="saved-empty-browse">
          Browse our homes <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/" className="alpha-btn-ghost">
          Back to home
        </Link>
      </div>
    </div>
  );
}

export default function SavedHomesPage() {
  const { items, count, remove, clear } = useSavedHomes();

  useSEO({
    title: "Saved homes · Compare your NDIS shortlist | Alpha Community Care",
    description:
      "Compare the SIL/SDA homes you've shortlisted at Alpha Community Care — accessibility features, availability, support ratios and enquiry in one place.",
    canonical: "https://alphacommunitycare.com.au/saved-homes",
    jsonLd: [breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Saved homes", path: "/saved-homes" },
    ])],
  });

  return (
    <div data-testid="page-saved-homes" className="bg-white">
      {/* HERO */}
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)] relative overflow-hidden">
        <div className="alpha-orb alpha-orb-purple w-[280px] h-[280px] -left-12 top-8 opacity-65 hidden md:block" aria-hidden />
        <div className="alpha-container pt-10 pb-7 md:pt-14 md:pb-10 relative">
          <nav className="text-xs text-[color:var(--alpha-mute)] flex items-center gap-1.5 mb-4">
            <Link to="/" className="hover:text-[color:var(--alpha-purple-700)]">Home</Link>
            <span>/</span>
            <span className="text-[color:var(--alpha-ink)] font-semibold">Saved homes</span>
          </nav>
          <div className="flex items-end gap-6 flex-wrap justify-between">
            <div>
              <div className="alpha-overline inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Your shortlist
              </div>
              <h1 className="mt-3 text-[32px] sm:text-5xl md:text-[56px] font-display font-medium tracking-tight leading-[1.04]">
                Compare your <span className="alpha-serif-italic alpha-gradient-text">saved homes</span>.
              </h1>
              <p className="mt-3 max-w-2xl text-[color:var(--alpha-mute)] text-[15px] md:text-[16px] leading-relaxed">
                Side-by-side comparison of the homes you've shortlisted. Saved on this device — no login, no clutter.
              </p>
            </div>
            {count > 0 && (
              <div className="flex items-center gap-2.5">
                <span className="alpha-chip alpha-chip-neutral !py-2 !px-3 !text-[12px]" data-testid="saved-list-count">
                  <Heart className="w-3.5 h-3.5 text-[color:var(--alpha-purple-700)] fill-current" />
                  {count} saved
                </span>
                <button
                  type="button"
                  onClick={clear}
                  data-testid="saved-clear"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-ink)]"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Clear all
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          {count === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Side-by-side scroller */}
              <div
                className="relative -mx-4 md:mx-0 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-4"
                data-testid="saved-compare"
              >
                <div className="flex gap-4 md:gap-5 px-4 md:px-0">
                  {items.map((p) => (
                    <PropertyColumn key={p.slug} p={p} onRemove={remove} />
                  ))}
                </div>
              </div>

              {/* Summary CTA below */}
              <div className="mt-7 md:mt-10 alpha-card p-6 md:p-8 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl alpha-gradient text-white flex items-center justify-center shadow-md">
                    <CheckCircle2 className="w-5 h-5" aria-hidden />
                  </span>
                  <div>
                    <div className="font-display font-semibold text-[16px] leading-tight">Ready to enquire?</div>
                    <div className="text-[13px] text-[color:var(--alpha-mute)] mt-0.5">
                      Mention your shortlist when you call — we'll bring all options to the conversation.
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  <Link
                    to="/referrals"
                    className="alpha-btn-primary"
                    data-testid="saved-cta-refer"
                  >
                    Make a referral <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href="tel:1300380692"
                    onClick={() => trackPhoneClick("1300 380 692", "saved-homes-summary")}
                    className="alpha-btn-green"
                    data-testid="saved-cta-phone"
                  >
                    <Phone className="w-4 h-4" /> 1300 380 692
                  </a>
                  <Link to="/vacancies" className="alpha-btn-ghost">
                    Browse all homes
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* TRUST FOOTER STRIP */}
      <section className="alpha-section-tight bg-[color:var(--alpha-bg-soft)] border-t border-[color:var(--alpha-line)]">
        <div className="alpha-container">
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-[12.5px] text-[color:var(--alpha-mute)]">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> Registered NDIS provider
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> Practice Standards aligned
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> 1:1 &amp; 1:2 support only
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> 24/7 on-call
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
